import React, {useEffect, useRef, useState} from "react";
import {Alert, Button, Descriptions, Divider, Popconfirm, Progress, Select, Table, message} from "antd";
import {CheckOutlined, LoadingOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import api from "../api";
import {handleApiError} from "../helpers/formatApiError";
import useConsumer from "../../channels/consumer";
import {entityTypeFields} from "./entityAssetFormPayload";
import {STANDARD_METADATA_FIELDS} from "./standardFields";

const STANDARD_ASSET_FIELDS = [
    "title",
    "headline",
    "description",
    "credit_line",
    "country",
    "state",
    "city",
    "sublocation",
    "event"
];

export function mappingValue(column) {
    const mapping = column.mapping;
    if (!mapping) return "raw";

    if (mapping.type === "custom_meta_field") return `custom_meta_field:${mapping.custom_meta_field_id}`;
    if (mapping.type === "asset_field") return `asset_field:${mapping.asset_field}`;
    if (mapping.type === "standard_metadata_field") return `standard_metadata_field:${mapping.standard_metadata_field}`;
    return mapping.type;
}

export function mappingOptions(entityType, t) {
    const fields = entityTypeFields(entityType);

    return [
        {value: "raw", label: t("archive-raw-field", "Archive as raw source field")},
        ...STANDARD_ASSET_FIELDS.map(field => ({
            value: `asset_field:${field}`,
            label: `${t("standard-asset-field", "Standard asset field")}: ${field}`
        })),
        ...STANDARD_METADATA_FIELDS.map(field => ({
            value: `standard_metadata_field:${field.key}`,
            label: `${t("standard-metadata-field", "Standard metadata field")}: ${t(field.key, field.label)}`
        })),
        ...fields.map(field => ({
            value: `custom_meta_field:${field.id}`,
            label: `${t("structured-field", "Structured field")}: ${field.name}`
        }))
    ];
}

export function decodeMappingValue(entityType, value) {
    if (value === "raw") return null;
    if (value === "filename" || value === "content") return {type: value};

    const [type, id] = value.split(":");
    if (type === "asset_field") return {type, asset_field: id};
    if (type === "standard_metadata_field") return {type, standard_metadata_field: id};
    if (type === "custom_meta_field") {
        const field = entityTypeFields(entityType).find(field => field.id === parseInt(id, 10));
        return {
            type,
            custom_meta_field_id: parseInt(id, 10),
            custom_meta_field_name: field?.name
        };
    }

    return null;
}

export function mappingLabel(entityType, column, t) {
    const value = mappingValue(column);
    if (value === "filename") return t("filename-label", "Filename / label");
    if (value === "content") return t("main-text", "Main Text");
    return mappingOptions(entityType, t).find(option => option.value === value)?.label;
}

export default function AssetSpreadsheetImportSetup({assetImport, onDone}) {
    const {t} = useTranslation();
    const [attrs, setAttrs] = useState(assetImport);
    const [progress, setProgress] = useState(0);
    const [processing, setProcessing] = useState(false);
    const [starting, setStarting] = useState(false);

    const consumer = useConsumer();
    const subscription = useRef();

    useEffect(() => {
        api.get(`/api/asset_spreadsheet_imports/${assetImport.id}`).then(res => {
            setAttrs(prev => ({...prev, ...res.data}));
        }).catch(handleApiError);

        if (consumer && !subscription.current) {
            subscription.current = consumer.subscriptions.create({channel: "UserChannel"}, {
                received: data => {
                    switch (data.type) {
                        case "assetSpreadsheetImport":
                            if (data.attrs?.id === assetImport.id) setAttrs(prev => ({...prev, ...data.attrs}));
                            return;

                        case "assetSpreadsheetImportProgress":
                            if (data.id === assetImport.id) {
                                if (typeof data.progress === "number") setProgress(data.progress);
                                if (data.done) {
                                    message.success(t("message-processing-done", "Processing Done!"));
                                    onDone && onDone();
                                }
                            }
                            return;
                    }
                }
            });
        }

        return () => {
            subscription.current?.unsubscribe();
            subscription.current = null;
        };
    }, [assetImport.id, consumer]);

    const updateColumnMapping = (column, value) => {
        api.put(`/api/asset_spreadsheet_imports/${assetImport.id}/mapping`, {
            column: {
                ...column,
                mapping: decodeMappingValue(attrs.entity_type, value)
            }
        }).then(res => {
            message.success(t("column-mapping-updated", "Column mapping updated."));
            setAttrs(prev => ({...prev, ...res.data}));
        }).catch(handleApiError);
    };

    const updateTextSources = values => {
        api.put(`/api/asset_spreadsheet_imports/${assetImport.id}`, {
            asset_spreadsheet_import: values
        }).then(res => {
            message.success(t("text-source-mapping-updated", "Text source mapping updated."));
            setAttrs(prev => ({...prev, ...res.data}));
        }).catch(handleApiError);
    };

    const startProcessing = () => {
        setStarting(true);
        api.post(`/api/asset_spreadsheet_imports/${assetImport.id}/start_process`).then(() => {
            setStarting(false);
            setProcessing(true);
        }).catch(error => {
            setStarting(false);
            handleApiError(error);
        });
    };

    const legacyFilenameSource = [...(attrs.columns || [])].reverse().find(column => column.mapping?.type === "filename")?.name;
    const legacyMainTextSource = (attrs.columns || []).find(column => column.mapping?.type === "content")?.name;
    const filenameSource = attrs.filename_source || legacyFilenameSource;
    const mainTextSource = attrs.main_text_source === null || attrs.main_text_source === undefined
        ? legacyMainTextSource
        : attrs.main_text_source;
    const filenameMapped = !!filenameSource;
    const mainTextMapped = !!mainTextSource;
    const sourceOptions = (attrs.columns || []).map(column => ({value: column.name, label: column.name}));
    const requirementErrors = attrs.processing_requirement_errors || (filenameMapped ? [] : [t("filename-mapping-required", "Map one column to Filename / label before processing.")]);
    const warnings = attrs.processing_warnings || (mainTextMapped ? [] : [t("main-text-mapping-warning", "No Main Text column is mapped. Assets will be created with structured fields only.")]);
    const readyForProcessing = attrs.ready_for_processing ?? requirementErrors.length === 0;

    if (processing) {
        return (
            <>
                <LoadingOutlined/> {t("processing-progress", "Processing progress")}:
                <Progress percent={progress || 0} status={"active"}/>
            </>
        );
    }

    if (attrs.aasm_state === "errored") {
        const error = attrs.parse_error || attrs.processing_error;
        return (
            <Alert
                type={"error"}
                showIcon
                message={attrs.parse_error ? t("parse-error", "Parse Error") : t("processing-error", "Processing Error")}
                description={`${t("import-error-description", "Unable to import file, please try another.")}${error?.message ? ` (${error.message})` : ""}`}
            />
        );
    }

    return (
        <>
            {(attrs.aasm_state === "analyzing" || attrs.aasm_state === "pending") && (
                <Alert
                    style={{marginBottom: "2em"}}
                    type={"info"}
                    showIcon
                    message={<em><LoadingOutlined/> {t("analyzing", "Analyzing")}...</em>}
                />
            )}

            <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label={t("filename", "Filename")}>{attrs.filename}</Descriptions.Item>
                <Descriptions.Item label={t("text-asset-type", "Text Asset Type")}>{attrs.entity_type?.name}</Descriptions.Item>
                <Descriptions.Item label={t("target-folder", "Target Folder")}>
                    {attrs.storage_folder?.path_names?.join(" / ") || attrs.storage_folder?.name || t("default-folder", "Default folder")}
                </Descriptions.Item>
                <Descriptions.Item label={t("rows", "Rows")}>{attrs.rows}</Descriptions.Item>
            </Descriptions>

            <Divider/>

            <Descriptions bordered size="small" column={1} style={{marginBottom: "1em"}}>
                <Descriptions.Item label={t("filename-label-source", "Filename / label source")}>
                    <Select
                        style={{width: "100%"}}
                        value={filenameSource}
                        options={sourceOptions}
                        onChange={value => updateTextSources({filename_source: value})}
                        getPopupContainer={() => document.body}
                        showSearch
                    />
                </Descriptions.Item>
                <Descriptions.Item label={t("main-text-source", "Main Text source")}>
                    <Select
                        allowClear
                        style={{width: "100%"}}
                        value={mainTextSource}
                        options={sourceOptions}
                        onChange={value => updateTextSources({main_text_source: value || ""})}
                        getPopupContainer={() => document.body}
                        showSearch
                    />
                </Descriptions.Item>
            </Descriptions>

            {!!requirementErrors.length && (
                <Alert
                    type="warning"
                    showIcon
                    style={{marginBottom: "1em"}}
                    message={t("import-needs-required-mapping", "Required mapping missing")}
                    description={requirementErrors.join(" ")}
                />
            )}

            {!requirementErrors.length && !!warnings.length && (
                <Alert
                    type="info"
                    showIcon
                    style={{marginBottom: "1em"}}
                    message={t("import-mapping-note", "Import mapping note")}
                    description={warnings.join(" ")}
                />
            )}

            <Table
                rowKey={column => `${attrs.id}-${column.index}-${column.name}`}
                size="small"
                dataSource={attrs.columns || []}
                pagination={false}
                columns={[
                    {
                        title: t("source-column", "Source Column"),
                        dataIndex: "name"
                    },
                    {
                        title: t("mapping", "Mapping"),
                        render: (_, column) => (
                            <Select
                                style={{width: "100%"}}
                                value={mappingValue(column)}
                                options={mappingOptions(attrs.entity_type, t)}
                                onChange={value => updateColumnMapping(column, value)}
                                optionFilterProp="label"
                                showSearch
                            />
                        )
                    }
                ]}
            />

            <Popconfirm title={t("confirm-process-import", "Process import?")} onConfirm={startProcessing}>
                <Button
                    icon={<CheckOutlined/>}
                    type={"primary"}
                    block
                    style={{marginTop: "2em"}}
                    loading={starting}
                    disabled={!["analyzed", "processed"].includes(attrs.aasm_state) || !readyForProcessing}
                >
                    {t("button-process-import", "Process Import")}
                </Button>
            </Popconfirm>
        </>
    );
}
